import { useTheme } from "@emotion/react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import ErrorDialog from "../../../components/dialogs/ErrorDialog.js";
import LoadingBackdrop from "../../../components/LoadingBackdrop.js";
import LogsList from "../../../components/page-item/LogsList.js";
import PageListHeader from "../../../components/page-list/PageListHeader.js";
import { useUser } from "../../../components/UserContext.js";
import { getEmployee, getLog } from "../../../lib/apiConfig.js";
import {
  AVATAR,
  PAGE_CONTAINER_SX,
  PAGE_TITLE_SX,
} from "../../../lib/constants.js";
import { formatDate } from "../../../lib/formatDate.js";
import { formatSecurityLog } from "../../../lib/formatSecurityLog.js";
import { useFetch } from "../../../lib/hooks/useFetch.js";

export default function EmployeeLogPage({ locales }) {
  const { runId } = useUser();
  const { state } = useLocation();
  const { palette } = useTheme();
  const [employeeId, setEmployeeId] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [logs, setLogs] = useState(null);
  const [formattedLogs, setFormattedLogs] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isAscending, setIsAscending] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const setError = (error) => {
    setErrorMessage(error.message);
    setShowError(true);
  };

  const { isFetching: isFetchingEmployee } = useFetch({
    endpoint: getEmployee({ id: employeeId, runId }),
    mustFetch: employeeId && !employee,
    onSuccess: setEmployee,
    onFail: setError,
  });

  const { isFetching: isFetchingLog } = useFetch({
    endpoint: getLog({ filter: `employeeId=${employeeId}`, runId }),
    mustFetch: employeeId && !logs,
    onSuccess: (response) => {
      setLogs(response);
      setFormattedLogs(
        response.map((log) => ({
          ...log,
          formattedLog: formatSecurityLog({ locales, log }),
        }))
      );
    },
    onFail: setError,
  });

  useEffect(() => {
    setEmployeeId(state.id);
  }, [state]);

  const filteredMessages = useMemo(() => {
    const filtered = formattedLogs.filter((log) =>
      log.formattedLog.toLowerCase().includes(searchValue.toLowerCase())
    );

    return filtered.sort(
      (a, b) =>
        (new Date(a.createdAt) - new Date(b.createdAt)) * (isAscending ? 1 : -1)
    );
  }, [isAscending, formattedLogs, searchValue]);

  const handleInput = ({ target }) => {
    setSearchValue(target.value);
  };

  const handleClearInput = () => {
    setSearchValue("");
  };

  const handleSort = () => {
    setIsAscending(!isAscending);
  };

  return !employee || isFetchingEmployee || isFetchingLog ? (
    <LoadingBackdrop />
  ) : (
    <Container disableGutters sx={PAGE_CONTAINER_SX}>
      <Box id="employee-log-header">
        <Typography component="h1" sx={PAGE_TITLE_SX}>
          {employee.name}
        </Typography>
        <Typography>{employee.department}</Typography>
      </Box>
      <Grid container id="employee-log-page">
        <Grid xs={12} smd={4} md={3}>
          <Avatar
            alt={`${employee?.name} avatar`}
            src={employee?.user?.imageUrl}
            sx={{
              boxShadow: `0 0 5px ${palette.divider}`,
              width: { xs: AVATAR.SIZE.SM, smd: AVATAR.SIZE.LG },
              height: { xs: AVATAR.SIZE.SM, smd: AVATAR.SIZE.LG },
            }}
          />
        </Grid>
        <Grid xs={12} smd={1} />
        <Grid xs={12} smd={7} md={8} sx={{ mt: { xs: 4, smd: 0 } }}>
          <PageListHeader
            handleClearInput={handleClearInput}
            handleInput={handleInput}
            handleSort={handleSort}
            locales={locales}
            isAscending={isAscending}
            pageName="employee-log"
            searchBoxLabel={locales["logs.search"]}
            searchValue={searchValue}
          />
          {filteredMessages.length ? (
            <LogsList
              id="employee-log"
              items={filteredMessages}
              renderPrimary={(log) => log.formattedLog}
              renderSecondary={(log) => formatDate(log.updatedAt)}
            />
          ) : (
            <Typography component="p">{locales["search.empty"]}</Typography>
          )}
        </Grid>
      </Grid>
      <ErrorDialog
        locales={locales}
        message={errorMessage}
        onClose={() => {
          setShowError(false);
          setErrorMessage(null);
        }}
        show={showError}
      />
    </Container>
  );
}
